import { createSelector } from '@reduxjs/toolkit';
import { SystemRolesName } from 'src/api';
import { selectCaseSelectedTranslation } from 'src/components/pages/Case/Case.slice';
import { getMessagingDisabled } from 'src/components/pages/Case/helpers/getMessagingDisabled';
import { ReportType } from 'src/graphql';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import { TRootState } from 'src/store';
export {
  editCaseTeamMemberAccess,
  removeCaseTeamMember,
} from './ReviewCaseTeamMemberModal.thunks';
import {
  CasePermissionsType,
  CaseTeamMemberPermissionsType,
  Nullable,
  TeamMemberStatus,
} from 'src/types';

export interface ReviewCaseTeamMemberModalVM {
  caseId: string;
  userId: number;
  name: string;
  site: string;
  role: string;
  country: string;
  status: TeamMemberStatus;
  caseDisplayId: string;
  caseTeamNoteText?: string;
  invitedAt: string;
  showEditCaseMemberModal: boolean;
  showRemoveCaseMemberModal: boolean;
  caseSelectedTranslation?: string;
  permissions: CaseTeamMemberPermissionsType[];
  filteredPermissions: CasePermissionsType[];
  canEditPermissions: boolean;
  unableToChat: boolean;
  caseLeadInviter: string | null;
  isReportAssignee: boolean;
  isSuperAdmin: boolean;
}

export const filterPermissions = <CasePermissionsType>(
  permissions: CaseTeamMemberPermissionsType[],
) => {
  const editPermissions = new Set<CaseTeamMemberPermissionsType>();

  permissions.forEach((permission) => {
    if (permission.startsWith('EDIT_')) {
      editPermissions.add(permission);
    }
  });

  const filteredPermissions = permissions.filter((permission) => {
    if (
      permission === 'VIEW_REPORT_MESSAGES' &&
      permissions.includes('SEND_REPORT_MESSAGES')
    ) {
      return false;
    }

    if (permission === 'ASSIGNABLE_TO_CASE_TEAM') {
      return false;
    }

    if (permission.startsWith('VIEW_')) {
      const correspondingEditPermission = permission.replace(
        'VIEW_',
        'EDIT_',
      ) as CasePermissionsType;
      return !editPermissions.has(
        correspondingEditPermission as CaseTeamMemberPermissionsType,
      );
    }

    return true;
  });

  return filteredPermissions as CasePermissionsType[];
};

const selectReviewCaseTeamMemberModalVM = createSelector(
  [
    (state: TRootState) => state.case.caseById,
    (state: TRootState) => state.case.showRemoveCaseMemberModal,
    (state: TRootState) => state.case.showEditCaseMemberModal,
    (state: TRootState) => state.case.selectedCaseTeamMemberPermissions,
    (state: TRootState) => selectCaseSelectedTranslation(state),
    (state: TRootState) => state.globalUsers.currentUser,
    (_state: TRootState, userId: Nullable<number>) => userId,
  ],
  (
    caseById,
    showRemoveCaseMemberModal,
    showEditCaseMemberModal,
    selectedCaseTeamMemberPermissions,
    caseSelectedTranslation,
    currentUser,
    userId,
  ): ReviewCaseTeamMemberModalVM => {
    if (!caseById || !userId) {
      return {
        caseId: '',
        userId: -1,
        name: '-',
        site: '-',
        role: '-',
        country: '-',
        status: '-',
        caseDisplayId: '',
        caseTeamNoteText: undefined,
        invitedAt: '',
        showEditCaseMemberModal: false,
        showRemoveCaseMemberModal: false,
        permissions: [],
        filteredPermissions: [],
        canEditPermissions: false,
        unableToChat: false,
        caseLeadInviter: '-',
        isReportAssignee: false,
        isSuperAdmin: false,
      };
    }

    const caseMembersData = caseById?.AllCaseTeamMembers?.find(
      (member) => member.id === userId,
    );

    const permissions = selectedCaseTeamMemberPermissions ?? [];

    let status: TeamMemberStatus = '-';
    if (
      caseMembersData?.Accounts?.some((account) =>
        account.Roles?.some(
          (role) =>
            role.SystemPermissions?.some(
              // eslint-disable-next-line max-nested-callbacks
              (permission) =>
                permission.permission ===
                SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD,
            ) &&
            role.SystemPermissions?.some(
              // eslint-disable-next-line max-nested-callbacks
              (permission) =>
                permission.permission ===
                SYSTEM_PERMISSIONS.ASSIGNABLE_TO_CASE_TEAM,
            ),
        ),
      )
    ) {
      status = 'Active';
    } else if (permissions.length > 0) {
      const canBeAssignedToTeam = permissions?.includes(
        'ASSIGNABLE_TO_CASE_TEAM',
      );

      if (canBeAssignedToTeam) {
        status = 'Active';
      }
    } else {
      status = 'Deactivated';
    }

    const caseDisplayId = formatCaseDisplayId(caseById?.readableId);
    const caseTeamMembers = caseById.CaseTeam;
    const caseTeamMember = caseTeamMembers.find(
      (member) => member.id === userId,
    );
    const caseTeamNoteText = caseTeamMember?.Note?.value;

    const invitedAt = caseTeamMember?.invitedAt;
    const caseLeadInviter = caseTeamMember?.Note?.inviterUser?.name ?? null;
    const isSuperAdmin = Boolean(
      caseTeamMember?.Roles?.includes(SystemRolesName.SUPER) ||
      caseTeamMember?.Roles?.includes(SystemRolesName.COI_SUPER),
    );
    const reportsAssignees = caseById?.Reports?.map(
      (report) => report?.Assignee?.id,
    ).filter((x) => x);
    const isReportAssignee = Boolean(reportsAssignees?.includes(userId ?? -1));

    return {
      caseId: caseById.id,
      userId,
      name: caseMembersData?.name ?? '-',
      site: caseMembersData?.site ?? '-',
      role: caseMembersData?.role ?? '-',
      country: caseMembersData?.country ?? '-',
      status,
      caseDisplayId,
      caseTeamNoteText: caseTeamNoteText,
      invitedAt,
      showEditCaseMemberModal: showEditCaseMemberModal ?? false,
      showRemoveCaseMemberModal: showRemoveCaseMemberModal ?? false,
      caseSelectedTranslation,
      permissions,
      filteredPermissions: filterPermissions(permissions),
      canEditPermissions:
        caseById?.CaseLead?.id.toString() === currentUser?.id?.toString(),
      unableToChat: getMessagingDisabled(caseById?.Reports?.[0] as ReportType),
      caseLeadInviter,
      isReportAssignee,
      isSuperAdmin,
    };
  },
);

export const reviewCaseTeamMemberModalVM =
  (userId: Nullable<number>) =>
  (state: TRootState): ReviewCaseTeamMemberModalVM =>
    selectReviewCaseTeamMemberModalVM(state, userId);

export const formatCaseDisplayId = (caseId?: string) =>
  caseId ? `${caseId.slice(0, 4)}-${caseId.slice(4)}` : '';
